import noUiSlider from 'nouislider';
import bind from 'lodash.bind';
import throttle from 'lodash.throttle';

class Slider {
  /**
   * @private
   * @type {Object.<string, noUiSlider>}
   * A collection of slider instances associated with their respective keys.
   * Slider instances are stored using its unique identifier.
   * Sliders can be accessed and manipulated programmatically
   * using their unique identifiers stored in this collection.
   */
  #sliders = {};

  /**
   * Creates an instance of BasicSlider.
   * @constructor
   * @param {NodeList} [sliderElements] - Optional parameter representing slider elements.
   */
  constructor(sliderElements) {
    this.createSlider = this.createSlider.bind(this);
    this.inputEventListener = this.inputEventListener.bind(this);

    this.initializeSliders(sliderElements);
  }

  /**
   * Initialize sliders for given elements, or use internal method for finding sliders.
   * @param {NodeList} [sliderElements] - Optional parameter containing slider elements.
   */
  initializeSliders(sliderElements = this.#findSliderElements()) {
    sliderElements?.forEach(this.createSlider);
  }

  /**
   * Find slider elements with class 'basic-slider__fieldset'.
   * @private
   * @returns {NodeList} Slider elements.
   */
  #findSliderElements() {
    return document.querySelectorAll('.basic-slider__fieldset');
  }

  /**
   * Find input elements associated with the slider element, having a class 'basic-slider__input'.
   * @param {HTMLElement} sliderElement - The slider element.
   * @private
   * @returns {number[]} An array of initial start values for the slider.
   */
  #initInputs(sliderElement) {
    // Prepare arrays for the actual slider DOM element, to which value display(s) can be pushed
    sliderElement.inputs = [];
    const parent = sliderElement.closest('.basic-slider');
    const startValues = [...parent.querySelectorAll('.basic-slider__input')].map((input, index) => {
      // Store a reference to these so we can connect to this later when updating the slider value in updateInputValues
      sliderElement.inputs[index] = input;
      input.addEventListener('change', this.inputEventListener);
      return parseInt(input.value);
    });
    return startValues;
  }

  /**
   * Extract slider data from a slider element.
   * @param {HTMLElement} sliderElement - The slider element.
   * @private
   * @returns {Object} Slider data.
   */
  #extractSliderData(sliderElement) {
    const min = parseInt(sliderElement.dataset.sliderMin);
    const max = parseInt(sliderElement.dataset.sliderMax);
    const step = parseInt(sliderElement.dataset.sliderStep);
    const range = { min, max };
    return { range, step };
  }

  /**
   * Create a noUiSlider instance for a slider element.
   * @param {HTMLElement} sliderParent - The slider element.
   */
  createSlider(sliderParent) {
    const sliderElement = sliderParent.querySelector('[role="slider"]');
    const startValues = this.#initInputs(sliderElement);
    const { range, step } = this.#extractSliderData(sliderElement);

    // When only one slider, show connector on low side. When two (or more?), show connector between sliders.
    // https://refreshless.com/nouislider/slider-options/#section-connect
    const connect = startValues.length < 2 ? 'lower' : true;

    const slider = noUiSlider.create(sliderElement, {
      start: startValues,
      behaviour: 'tap',
      connect: connect,
      step: step,
      range: range,
    });

    // Bind an event listener for when the slider is updated
    slider.on('update', throttle(bind(this.updateInputValues, slider), 1000, { trailing: true, leading: false }));

    this.#sliders[sliderElement.id] = slider;

    sliderElement.setAttribute('tabindex', -1);
    const parent = sliderElement.closest('.basic-slider');

    // Update aria-controls attribute for each slider
    const knobs = parent.querySelectorAll('.noUi-handle');
    knobs.forEach((knob, _index) => {
      try {
        knob.setAttribute('tabindex', -1);
      } catch (e) {
        console.debug(e);
      }
    });
  }

  /**
   * Update input values with slider values.
   * @param {number[]} values - The slider values.
   */
  updateInputValues(values) {
    const { inputs } = this.target;
    values.forEach((input, index) => {
      const intValue = parseInt(input);
      if (parseInt(inputs[index].value) !== intValue) {
        inputs[index].value = intValue;
        // If the field is in a form, with this we can listen on the form for changes in the input fields when dragging the slider updates the field value.
        inputs[index].closest('form')?.dispatchEvent(new CustomEvent('change', { detail: { target: inputs[index] } }));
      }
    });
  }

  /**
   * Updates the slider values when input fields are modified.
   * This ensures users can adjust the slider either by dragging it or by typing values into input fields.
   * @param {Event} event - The input event triggered by a change in an input field.
   */
  inputEventListener(event) {
    // Find the slider container element
    const parent = event.target.closest('.basic-slider');

    // Select all input fields within the slider
    const inputs = parent.querySelectorAll('.basic-slider__input');
    // Extract the values
    const newValues = [...inputs].map(input => parseInt(input.value)).sort((a, b) => a - b);

    const { parentId } = event.target.dataset;
    this.#sliders[parentId].set(newValues);
  }
}

export { Slider };
